import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, DownloadSimple, MagnifyingGlass, Package, X } from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import BuildsTabs from '../components/BuildsTabs';
import LifecycleShell from '../components/LifecycleShell';
import Spinner from '../components/Spinner';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import Config from '../config/Config';
import { downloadAuthenticatedFile, fetchJson, getFriendlyErrorMessage } from '../utils/api';

const baseUrl = Config.ApiBaseUrl;

const formatArtifactDate = (artifact) => artifact?.timestamp
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(Number(artifact.timestamp) * 1000))
  : artifact?.creation_date;

const isArtifactDownloadUrl = (url) => {
  try {
    const requestedUrl = new URL(url);
    const apiUrl = new URL(baseUrl);
    return requestedUrl.origin === apiUrl.origin && requestedUrl.pathname.startsWith(`${apiUrl.pathname}download/`);
  } catch {
    return false;
  }
};

// A protected artifact is fetched whole before the browser saves it, which can take a while for a
// large APK/AAB. `downloading` lets the button show that something is happening and ignore repeat clicks.
function useArtifactDownload(artifact, t, onActiveChange) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const startDownload = async (event) => {
    event.preventDefault();
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    onActiveChange(artifact.filename, true);
    try {
      await downloadAuthenticatedFile(`${baseUrl}${artifact.url}`, artifact.filename);
    } catch (error) {
      if (error.status !== 401) setDownloadError(t.loadDownloadsFailed);
    } finally {
      setDownloading(false);
      onActiveChange(artifact.filename, false);
    }
  };

  return { downloading, downloadError, startDownload };
}

function ArtifactAction({ artifact, type, t, onActiveChange }) {
  const { downloading, downloadError, startDownload } = useArtifactDownload(artifact, t, onActiveChange);

  if (!artifact) {
    return <span className="btn btn--pill artifact" aria-disabled="true"><span><strong>{type.toUpperCase()}</strong><small>{t.notAvailable}</small></span></span>;
  }

  return <span className="stack stack--sm">
    <a className="btn btn--pill artifact" href={`${baseUrl}${artifact.url}`} onClick={startDownload} aria-busy={downloading}>
      <span><strong>{type.toUpperCase()}</strong><small>{downloading ? t.downloadingFile : formatArtifactDate(artifact)}</small></span>
      {downloading ? <Spinner /> : <DownloadSimple size={20} aria-hidden="true" />}
    </a>
    <span className="sr-only" role="status">{downloading ? t.downloadingFile : ''}</span>
    {downloadError && <span className="error-text" role="alert">{downloadError}</span>}
  </span>;
}

function HistoryArtifactAction({ artifact, t, onActiveChange }) {
  const { downloading, downloadError, startDownload } = useArtifactDownload(artifact, t, onActiveChange);

  return <li>
    <a className="row" href={`${baseUrl}${artifact.url}`} onClick={startDownload} aria-busy={downloading}>
      <span className="row__main"><span className="row__title">{artifact.filename}</span><span className="row__meta">{downloading ? t.downloadingFile : formatArtifactDate(artifact)}</span></span>
      <span className="row__side"><span className="chip">{artifact.type.toUpperCase()}</span>{downloading ? <Spinner /> : <DownloadSimple size={20} aria-hidden="true" />}</span>
    </a>
    <span className="sr-only" role="status">{downloading ? t.downloadingFile : ''}</span>
    {downloadError && <p className="error-text" role="alert">{downloadError}</p>}
  </li>;
}

function ArtifactRow({ group, filters, t, onActiveChange }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('idle');
  const [historyError, setHistoryError] = useState('');

  const fetchHistory = async () => {
    setHistoryStatus('loading');
    setHistoryError('');
    const params = new URLSearchParams({ type: filters.type, sort: filters.sort });
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    try {
      const data = await fetchJson(`${baseUrl}artifacts/${encodeURIComponent(group.flavor)}/history?${params}`);
      setHistory(data.items || []);
      setHistoryStatus('ready');
    } catch (error) {
      setHistoryError(getFriendlyErrorMessage(error, { network: t.backendUnavailable, server: t.serverError, generic: t.loadHistoryFailed }));
      setHistoryStatus('error');
    }
  };

  const loadHistory = () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (historyStatus !== 'ready') fetchHistory();
  };

  return <article className="card stack">
    <div className="toolbar">
      <div className="cluster">
        <span className="mark" aria-hidden="true"><Package size={20} /></span>
        <div><h2>{group.flavor}</h2><p className="card__sub">{t.artifactCount.replace('{count}', group.total_artifacts)}</p></div>
      </div>
      <div className="cluster">
        <ArtifactAction artifact={group.latest?.apk} type="apk" t={t} onActiveChange={onActiveChange} />
        <ArtifactAction artifact={group.latest?.aab} type="aab" t={t} onActiveChange={onActiveChange} />
      </div>
    </div>
    <button className="btn btn--text" type="button" onClick={loadHistory} aria-expanded={expanded} disabled={!group.history_count}>
      <span>{group.history_count ? t.buildHistory.replace('{count}', group.history_count) : t.noHistory}</span><ArrowDown size={18} aria-hidden="true" />
    </button>
    {expanded && <div className="stack stack--sm" aria-live="polite">
      {historyStatus === 'loading' && <p className="hint">{t.loadingHistory}</p>}
      {historyStatus === 'error' && <p className="notice notice--error" role="alert"><span>{historyError}</span> <button className="btn btn--pill" type="button" onClick={fetchHistory}>{t.tryAgain}</button></p>}
      {historyStatus === 'ready' && <ul className="row-list">{history.map((artifact) => <HistoryArtifactAction artifact={artifact} key={artifact.url} t={t} onActiveChange={onActiveChange} />)}</ul>}
    </div>}
  </article>;
}

function DownloadsPage() {
  const { language } = useLanguage();
  const t = translations[language];
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [type, setType] = useState(searchParams.get('type') || 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'date_new');
  const [page, setPage] = useState(Math.max(1, Number(searchParams.get('page')) || 1));
  const [pageSize, setPageSize] = useState([10, 20, 50].includes(Number(searchParams.get('page_size'))) ? Number(searchParams.get('page_size')) : 10);
  const [catalog, setCatalog] = useState({ items: [], page: 1, page_size: 10, total_items: 0, total_pages: 0 });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [resumingDownload, setResumingDownload] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState([]);
  const trackDownload = useCallback((filename, active) => {
    setDownloadingFiles((current) => {
      if (active) return [...current, filename];
      const next = [...current];
      const index = next.indexOf(filename);
      if (index >= 0) next.splice(index, 1);
      return next;
    });
  }, []);
  const invalidDateRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  useEffect(() => {
    const downloadUrl = searchParams.get('download');
    if (!downloadUrl) return undefined;

    let active = true;
    const resumeDownload = async () => {
      if (!isArtifactDownloadUrl(downloadUrl)) {
        if (active) setError(t.loadDownloadsFailed);
        return;
      }
      setResumingDownload(true);
      try {
        const pathname = new URL(downloadUrl).pathname;
        await downloadAuthenticatedFile(downloadUrl, decodeURIComponent(pathname.slice(pathname.lastIndexOf('/') + 1)));
      } catch (downloadError) {
        if (active && downloadError.status !== 401) setError(t.loadDownloadsFailed);
      } finally {
        // Cleared even when the effect was replaced meanwhile, otherwise the notice would stay up.
        setResumingDownload(false);
        if (active) {
          const next = new URLSearchParams(searchParams);
          next.delete('download');
          setSearchParams(next, { replace: true });
        }
      }
    };

    resumeDownload();
    return () => { active = false; };
  }, [searchParams, setSearchParams, t.loadDownloadsFailed]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQuery) next.set('q', debouncedQuery);
    if (type !== 'all') next.set('type', type);
    if (dateFrom) next.set('date_from', dateFrom);
    if (dateTo) next.set('date_to', dateTo);
    if (sort !== 'date_new') next.set('sort', sort);
    if (page !== 1) next.set('page', String(page));
    if (pageSize !== 10) next.set('page_size', String(pageSize));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [dateFrom, dateTo, debouncedQuery, page, pageSize, searchParams, setSearchParams, sort, type]);

  const requestParams = useMemo(() => {
    const params = new URLSearchParams({ type, sort, page: String(page), page_size: String(pageSize) });
    if (debouncedQuery) params.set('q', debouncedQuery);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params;
  }, [dateFrom, dateTo, debouncedQuery, page, pageSize, sort, type]);

  const loadCatalog = useCallback(async () => {
    if (invalidDateRange) return;
    setStatus('loading');
    setError('');
    try {
      const data = await fetchJson(`${baseUrl}artifacts?${requestParams}`);
      const resolvedPage = Number(data?.page) || 1;
      setCatalog({
        items: Array.isArray(data?.items) ? data.items : [],
        page: resolvedPage,
        page_size: Number(data?.page_size) || pageSize,
        total_items: Number(data?.total_items) || 0,
        total_pages: Number(data?.total_pages) || 0,
      });
      if (resolvedPage !== page) setPage(resolvedPage);
      setStatus('ready');
    } catch (loadError) {
      setCatalog((current) => ({ ...current, items: [] }));
      setError(getFriendlyErrorMessage(loadError, { network: t.backendUnavailable, server: t.serverError, generic: t.loadDownloadsFailed }));
      setStatus('error');
    }
  }, [invalidDateRange, page, pageSize, requestParams, t.backendUnavailable, t.loadDownloadsFailed, t.serverError]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const resetFilters = () => {
    setQuery(''); setDebouncedQuery(''); setType('all'); setDateFrom(''); setDateTo(''); setSort('date_new'); setPage(1); setPageSize(10);
  };
  const changeFilter = (setter) => (event) => { setter(event.target.value); setPage(1); };
  const filters = { type, dateFrom, dateTo, sort };

  return <LifecycleShell title={t.downloadsTitle} eyebrow={t.latestArtifacts} lead={t.downloadsSub} action={<BuildsTabs />}>
    {downloadingFiles.length > 0 && <p className="notice" role="status"><Spinner /><span>{t.downloadingNamed.replace('{file}', downloadingFiles.join(', '))}</span></p>}
    <section className="card stack" aria-label={t.downloadFilters}>
      <div className="form-grid">
        <div className="field field--wide">
          <label className="label" htmlFor="catalog-search">{t.searchFlavor}</label>
          <div className="search" style={{ maxWidth: 'none' }}>
            <MagnifyingGlass size={20} aria-hidden="true" />
            <input id="catalog-search" className="input" type="search" value={query} onChange={changeFilter(setQuery)} placeholder={t.findFlavorName} />
          </div>
        </div>
        <div className="field"><label className="label" htmlFor="catalog-type">{t.artifactType}</label><select id="catalog-type" className="select" value={type} onChange={changeFilter(setType)}><option value="all">{t.allTypes}</option><option value="apk">APK</option><option value="aab">AAB</option></select></div>
        <div className="field"><label className="label" htmlFor="catalog-from">{t.dateFrom}</label><input id="catalog-from" className="input" type="date" value={dateFrom} onChange={changeFilter(setDateFrom)} /></div>
        <div className="field"><label className="label" htmlFor="catalog-to">{t.dateTo}</label><input id="catalog-to" className="input" type="date" value={dateTo} onChange={changeFilter(setDateTo)} aria-invalid={invalidDateRange} /></div>
        <div className="field"><label className="label" htmlFor="catalog-sort">{t.sortBy}</label><select id="catalog-sort" className="select" value={sort} onChange={changeFilter(setSort)}><option value="date_new">{t.newestFirst}</option><option value="date_old">{t.oldestFirst}</option><option value="name_asc">{t.nameAscending}</option><option value="name_desc">{t.nameDescending}</option></select></div>
      </div>
      <div className="btn-row"><button className="btn" type="button" onClick={resetFilters}><X size={18} aria-hidden="true" />{t.clearFilters}</button></div>
    </section>
    {resumingDownload && <p className="notice" role="status"><Spinner /><span>{t.preparingDownload}</span></p>}
    {invalidDateRange && <p className="notice notice--error" role="alert">{t.invalidDateRange}</p>}
    <p className="hint" aria-live="polite">{invalidDateRange ? t.invalidDateRange : status === 'ready' ? t.flavorResults.replace('{count}', catalog.total_items) : t.loadingArtifacts}</p>
    {status === 'loading' && !invalidDateRange && <p className="hint" role="status">{t.loadingArtifacts}</p>}
    {status === 'error' && <div className="notice notice--error" role="alert"><span>{error}</span><button className="btn btn--pill" type="button" onClick={loadCatalog}>{t.tryAgain}</button></div>}
    {status === 'ready' && !invalidDateRange && catalog.items.length === 0 && <div className="card empty"><Package size={28} aria-hidden="true" /><strong>{t.noBuildsFoundTitle}</strong><span>{t.tryAdjustingSearch}</span><button className="btn" type="button" onClick={resetFilters}>{t.clearAllFilters}</button></div>}
    {status === 'ready' && !invalidDateRange && catalog.items.length > 0 && <div className="stack">{catalog.items.map((group) => <ArtifactRow key={`${group.flavor}-${type}-${dateFrom}-${dateTo}-${sort}`} group={group} filters={filters} t={t} onActiveChange={trackDownload} />)}</div>}
    {status === 'ready' && !invalidDateRange && catalog.total_pages > 0 && <nav className="pager" aria-label={t.paginationLabel}>
      <label className="cluster"><span>{t.rowsPerPage}</span><select className="select" style={{ width: 'auto' }} value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
      <span>{t.pageOf.replace('{page}', catalog.page).replace('{total}', catalog.total_pages)}</span>
      <div className="btn-row"><button className="btn" type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1} aria-label={t.previousPage}><ArrowLeft size={18} /></button><button className="btn" type="button" onClick={() => setPage((value) => Math.min(catalog.total_pages, value + 1))} disabled={page >= catalog.total_pages} aria-label={t.nextPage}><ArrowRight size={18} /></button></div>
    </nav>}
  </LifecycleShell>;
}

export default DownloadsPage;
